import React, { useRef, useState, useEffect, useCallback } from "react";
import noImage from "../../assets/no_image.png";
import { normalizeUrl } from "../../utils/helpers/normalizeUrl";

const loadedUrls = new Set();

const OptimizedImage = ({
  src,
  alt = "",
  width,
  height,
  className = "",
  style = {},
  srcSet,
  sizes,
  placeholder,
  fallback = noImage,
  thumbnailSrc,
  viewMargin = "200px",
  onError,
  ...props
}) => {
  const imgRef = useRef(null);
  const [imgSrc, setImgSrc] = useState(placeholder || "");
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);

  // Per-URL guard: only fire `onError` once per failed URL so a genuine 404 (or a
  // URL that stays broken after a refresh) doesn't loop into the caller's refresh.
  const erroredUrlRef = useRef(null);

  const displaySrc = thumbnailSrc || src;

  // Intersection Observer — lazy load
  useEffect(() => {
    if (!displaySrc) return;

    const imgElement = imgRef.current;
    if (!imgElement) return;

    setIsLoaded(false);
    setHasError(false);
    // New URL → clear the per-URL error guard so a refreshed URL may report its
    // own failure. (Same URL re-render keeps the guard, preventing a 404 loop.)
    erroredUrlRef.current = null;

    // If already loaded, show immediately
    if (loadedUrls.has(displaySrc)) {
      setImgSrc(displaySrc);
      setIsLoaded(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setImgSrc(displaySrc);
            observer.disconnect();
          }
        });
      },
      { rootMargin: viewMargin }, // for smoother scroll
    );

    observer.observe(imgElement);

    return () => {
      observer.disconnect();
    };
  }, [displaySrc, viewMargin]);

  const handleError = useCallback(() => {
    if (!hasError) {
      setImgSrc(fallback);
      setHasError(true);
    }
    // Notify the caller once per failed URL so it can opt into a signed-URL
    // refresh (e.g. onError={refreshNow}) without looping on a real 404.
    if (typeof onError === "function" && erroredUrlRef.current !== displaySrc) {
      erroredUrlRef.current = displaySrc;
      onError(displaySrc);
    }
  }, [fallback, hasError, onError, displaySrc]);

  const handleLoad = useCallback(() => {
    const currentSrc = imgRef.current?.currentSrc;
    const expectedSrc = normalizeUrl(displaySrc);
    if (currentSrc !== expectedSrc) return;

    setIsLoaded(true);
    loadedUrls.add(displaySrc);
  }, [displaySrc]);

  // Fixed dimensions prevent layout shift
  const containerStyle = {
    width: style.width || width,
    height: style.height || height,
    ...style,
  };

  return (
    <img
      ref={imgRef}
      src={imgSrc || placeholder || fallback}
      alt={alt}
      width={width}
      height={height}
      srcSet={isLoaded ? srcSet : undefined}
      sizes={isLoaded ? sizes : undefined}
      onError={handleError}
      onLoad={handleLoad}
      className={className}
      style={{
        ...containerStyle,
        filter: !isLoaded && !hasError ? "blur(10px)" : "none",
        transition: "filter 0.3s ease",
        backgroundColor: !isLoaded ? "#f3f4f6" : "transparent",
      }}
      loading="lazy"
      decoding="async"
      {...props}
    />
  );
};

export default React.memo(OptimizedImage);
